/**
 * WorkflowAlertsCard — Owner / Administrator dashboard widget that
 * surfaces stale records across three workflow gates:
 *
 *   • Contracts signed >= 14d ago with no project linked yet
 *   • Invoices stuck in Draft >= 7d
 *   • Active projects with no status log entry in >= 30d
 *
 * Backed by /api/workflow-alerts. Each row deep-links to that record's
 * detail panel or list view so users can act immediately.
 */
import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, FileText, Receipt, FolderKanban, ChevronRight } from 'lucide-react';
import api from '@/lib/api';

export function WorkflowAlertsCard({ navigate }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState({ contracts: true, invoices: true, projects: true });

  useEffect(() => {
    api.get('/workflow-alerts').then(r => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return null;
  const total = data.stale_contracts.length + data.stale_invoices.length + data.stale_projects.length;
  if (total === 0) {
    return (
      <Card className="border-white/10 bg-card shadow-none" data-testid="workflow-alerts-card-empty">
        <CardHeader className="pb-2 border-b border-white/[0.08]">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <AlertTriangle className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
            Workflow Alerts
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <p className="text-xs text-muted-foreground">All workflow gates are clean. No stale records.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-white/10 bg-card shadow-none" data-testid="workflow-alerts-card">
      <CardHeader className="pb-2 border-b border-white/[0.08]">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <AlertTriangle className="h-3.5 w-3.5 text-amber-400 shrink-0" />
          Workflow Alerts
          <span className="text-xs text-muted-foreground/70 ml-auto tabular-nums">{total}</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 pt-3">
        {data.stale_contracts.length > 0 && (
          <Section
            icon={<FileText className="h-3 w-3 text-violet-400" />}
            title="Contracts without a project"
            threshold={`≥ ${data.thresholds.contract_days}d`}
            count={data.stale_contracts.length}
            open={open.contracts}
            onToggle={() => setOpen(s => ({ ...s, contracts: !s.contracts }))}
            items={data.stale_contracts}
            renderRow={(c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => navigate(`/contracts?contract=${c.id}`)}
                className="w-full flex items-center justify-between py-1.5 border-b border-white/[0.06] last:border-0 hover:bg-white/[0.025] transition-colors text-left px-1"
                data-testid={`alert-contract-${c.id}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-mono text-primary truncate">{c.contract_number}</p>
                  <p className="text-xs text-muted-foreground truncate">{c.title} · {c.client_name}</p>
                </div>
                <span className="text-xs text-amber-400 tabular-nums shrink-0 ml-2">{c.days_since}d</span>
              </button>
            )}
          />
        )}
        {data.stale_invoices.length > 0 && (
          <Section
            icon={<Receipt className="h-3 w-3 text-emerald-400" />}
            title="Drafts not yet reviewed"
            threshold={`≥ ${data.thresholds.invoice_days}d`}
            count={data.stale_invoices.length}
            open={open.invoices}
            onToggle={() => setOpen(s => ({ ...s, invoices: !s.invoices }))}
            items={data.stale_invoices}
            renderRow={(inv) => (
              <button
                key={inv.id}
                type="button"
                onClick={() => navigate(`/invoices?invoice=${inv.id}`)}
                className="w-full flex items-center justify-between py-1.5 border-b border-white/[0.06] last:border-0 hover:bg-white/[0.025] transition-colors text-left px-1"
                data-testid={`alert-invoice-${inv.id}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-mono text-primary truncate">{inv.invoice_number}</p>
                  <p className="text-xs text-muted-foreground truncate">{inv.project_name || inv.client_name}</p>
                </div>
                <span className="text-xs text-amber-400 tabular-nums shrink-0 ml-2">{inv.days_since}d</span>
              </button>
            )}
          />
        )}
        {data.stale_projects.length > 0 && (
          <Section
            icon={<FolderKanban className="h-3 w-3 text-primary" />}
            title="Projects without a recent status update"
            threshold={`≥ ${data.thresholds.project_days}d`}
            count={data.stale_projects.length}
            open={open.projects}
            onToggle={() => setOpen(s => ({ ...s, projects: !s.projects }))}
            items={data.stale_projects}
            renderRow={(p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => navigate(`/projects/${p.id}?tab=status`)}
                className="w-full flex items-center justify-between py-1.5 border-b border-white/[0.06] last:border-0 hover:bg-white/[0.025] transition-colors text-left px-1"
                data-testid={`alert-project-${p.id}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-mono text-primary truncate">{p.project_number}</p>
                  <p className="text-xs text-muted-foreground truncate">{p.name} · {p.status}</p>
                </div>
                <span className="text-xs text-amber-400 tabular-nums shrink-0 ml-2">{p.days_since}d</span>
              </button>
            )}
          />
        )}
      </CardContent>
    </Card>
  );
}

function Section({ icon, title, threshold, count, open, onToggle, items, renderRow }) {
  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-2 py-1.5 text-left hover:text-foreground transition-colors"
      >
        {icon}
        <span className="text-xs font-medium">{title}</span>
        <span className="text-xs text-muted-foreground/70">({threshold})</span>
        <span className="ml-auto text-xs text-muted-foreground tabular-nums">{count}</span>
        <ChevronRight className={`h-3 w-3 text-muted-foreground transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>
      {open && <div className="space-y-0">{items.map(renderRow)}</div>}
    </div>
  );
}
